/**
 * Barra superior y navegación entre secciones.
 *
 * Las secciones que aún no existen aparecen desactivadas y etiquetadas con la
 * fase en que llegan, en vez de ocultarse. Es información útil: quien abre el
 * proyecto ve el mapa completo, y en las pruebas nadie pierde el tiempo buscando
 * una pantalla que todavía no se ha construido.
 *
 * En móvil solo quedan la marca y los saldos: tres pestañas y dos contadores no
 * caben en 360 px sin apretujarse.
 */

import { Home, Library, ShoppingBag, Store } from 'lucide-react';
import { CurrencyHeader } from './CurrencyHeader';
import { VIPBadge } from './VIPBadge';

export type SectionId = 'play' | 'collection' | 'shop' | 'market';

interface Section {
  id: SectionId;
  label: string;
  icon: typeof Home;
  /** Fase del plan en la que se implementa. undefined = ya disponible. */
  comingIn?: string;
}

const SECTIONS: Section[] = [
  { id: 'play', label: 'Partida', icon: Home },
  { id: 'collection', label: 'Colección', icon: Library },
  { id: 'shop', label: 'Tienda', icon: ShoppingBag },
  { id: 'market', label: 'Mercado', icon: Store },
];

interface NavbarProps {
  active: SectionId;
  gold: number;
  gems: number;
  isVip: boolean;
  /** Ausente en modo local, donde no hay cuenta. */
  username?: string;
  onNavigate?: (section: SectionId) => void;
}

export function Navbar({ active, gold, gems, isVip, username, onNavigate }: NavbarProps) {
  return (
    <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-slate-950/80 px-2 py-1.5 backdrop-blur-md sm:px-4 sm:py-2">
      <div className="flex items-center gap-3">
        <span className="bg-linear-to-r from-amber-300 to-rose-400 bg-clip-text text-sm font-black tracking-tight text-transparent sm:text-lg">
          TRÍADA
        </span>

        <nav className="hidden items-center gap-1 sm:flex">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const locked = Boolean(section.comingIn);
            return (
              <button
                key={section.id}
                type="button"
                disabled={locked}
                onClick={() => onNavigate?.(section.id)}
                title={locked ? `Disponible en la ${section.comingIn}` : section.label}
                className={[
                  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold transition',
                  active === section.id
                    ? 'bg-white/15 text-white'
                    : 'text-slate-400 hover:bg-white/10 hover:text-slate-200',
                  locked ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex items-center gap-2">
        {/* Oculto en móvil: el nombre compite por sitio con los dos contadores,
            y saber el saldo importa más que ver el propio nombre. */}
        {username ? (
          <span className="hidden max-w-28 truncate text-xs font-medium text-slate-300 sm:inline">
            {username}
          </span>
        ) : null}
        <VIPBadge active={isVip} />
        <CurrencyHeader gold={gold} gems={gems} />
      </div>
    </header>
  );
}
